<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from "vue";

import actions from "~/actions";
import ConfirmationDialog from "~/components/dumb/ConfirmationDialog.vue";
import MultipleEmailInput from "~/components/dumb/MultipleEmailInput.vue";
import SimpleTable from "~/components/dumb/SimpleTable.vue";
import SettingsUpgradeCtas from "~/components/settings/SettingsUpgradeCtas.vue";
import { UsersRemoveIcon } from "~/icons";
import { makeLinkToSettingsRef } from "~/router/common";
import { DialogMode, Entitlement, Subscription } from "~/shared/enums";
import type { SimpleTableColumn, User } from "~/shared/types";
import { useAppStore, useDataStore, usePageStore, useTenantStore, useUserStore } from "~/stores";
import ActionsCell from "~/views/settings/UserCells/ActionsCell.vue";
import AvatarCell from "~/views/settings/UserCells/AvatarCell.vue";
import RoleCell from "~/views/settings/UserCells/RoleCell.vue";
import StatusCell from "~/views/settings/UserCells/StatusCell.vue";
import { useSettingsFooter } from "~/views/settings/useSettings";

const appStore = useAppStore();
const dataStore = useDataStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();
const userStore = useUserStore();

const allUsers = computed(() => dataStore.getUserList({ excludeAgents: true, includeSpecialists: true }));

const hasAccessToRoles = computed(() => tenantStore.getEntitlementValue(Entitlement.ROLES));
const upgradeRequired = computed(() => {
  const userLimit = tenantStore.getEntitlementValue(Entitlement.MAX_USERS) as number;
  return tenantStore.billedUserCount >= userLimit;
});

const userToRemove = ref<User | null>(null);
const removeUserModal = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const userRemovalConfirmationText = computed(
  () =>
    `Are you sure you want to ${
      userStore.duid === userToRemove.value?.duid
        ? "leave this workspace?"
        : `remove ${userToRemove.value?.name || userToRemove.value?.email} from this workspace?`
    } This action cannot be undone.`
);

const removeUser = () => {
  if (!userToRemove.value) {
    return;
  }

  dataStore.removeUser(userToRemove.value);
};

const getContextMenuSections = (user: User) =>
  actions.context.user(user, (eventKind: string) => {
    switch (eventKind) {
      case "removeUser": {
        userToRemove.value = user;
        removeUserModal.value?.openModal();
        break;
      }
      default: {
        throw new Error(`Unknown event kind: ${eventKind}`);
      }
    }
  });

const onContextMenu = (event: MouseEvent, user: User) => {
  if (tenantStore.isDart && !pageStore.adminHidden && event.altKey) {
    return;
  }
  appStore.openContextMenu(event as PointerEvent, getContextMenuSections(user));
};

const columns = computed(() => {
  const res: SimpleTableColumn<User>[] = [
    {
      title: "User",
      hideTitle: true,
      cell: AvatarCell,
      classes: "w-full max-w-0 pr-2",
    },
    {
      title: "Status",
      cell: StatusCell,
      classes: "pr-2",
      headerClasses: "min-w-20 text-left",
    },
  ];

  if (hasAccessToRoles.value) {
    res.push({
      title: "Role",
      cell: RoleCell,
      classes: "w-28",
      headerClasses: "pl-2 min-w-28 text-left",
    });
  }

  res.push({
    title: "Actions",
    hideTitle: true,
    cell: ActionsCell,
    getCellArgs: () => ({
      getContextMenuSections,
    }),
    classes: "w-6",
    headerClasses: "w-6",
  });

  return res;
});

const agentsLink = makeLinkToSettingsRef("agents");

const { setFooterContent } = useSettingsFooter();

onMounted(() => {
  setFooterContent({ text: "Add an agent instead", link: agentsLink.value });
});
onUnmounted(() => {
  setFooterContent(null);
});
</script>

<template>
  <div>
    <ConfirmationDialog
      ref="removeUserModal"
      :mode="DialogMode.DELETE"
      :title="userStore.duid === userToRemove?.duid ? 'Leave workspace' : 'Remove user'"
      :description="userRemovalConfirmationText"
      :confirm-text="userStore.duid === userToRemove?.duid ? 'Leave' : 'Remove'"
      cancel-text="Cancel"
      :icon="UsersRemoveIcon"
      @confirm="removeUser" />

    <div class="flex flex-col">
      <MultipleEmailInput :users="allUsers" @submit="(emails, role) => actions.auth.inviteUsers(role, emails)" />

      <SimpleTable :columns="columns" :rows="allUsers" @context-menu="onContextMenu" />

      <SettingsUpgradeCtas v-if="upgradeRequired" :subscription="Subscription.PREMIUM" class="mt-6" />
    </div>
  </div>
</template>
